// Approve a held signup: plan, trial length, web address, which features to
// start with, and the welcome email — with a live preview of exactly what
// will be sent (the server builds preview, test and real send with the same
// function, trialActivation.buildWelcomeEmail).
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  XMarkIcon, CheckIcon, ExclamationTriangleIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import api from '../../api/axios'
import { getPlans } from '../../api/features'
import type { Composer, ComposerOptions, SignupRow } from './types'
import { PORTAL_DOMAIN, TIER_ORDER, ago, errMsg, tierLabel } from './types'

function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}
      className={`relative w-9 h-5 flex-none rounded-full transition-colors ${on ? 'bg-blue-600' : 'bg-slate-300'}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-[left] ${on ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  )
}

const inTier = (requires: string, tier: string) => TIER_ORDER.indexOf(requires) <= TIER_ORDER.indexOf(tier)

export default function ApprovalDrawer({ row, options, onClose, onApproved, notify }: {
  row: SignupRow
  options: ComposerOptions
  onClose: () => void
  onApproved: () => void
  notify: (msg: string, tone?: 'ok' | 'error') => void
}) {
  const [tier, setTier] = useState('standard')
  const [trialDays, setTrialDays] = useState(90)
  const [slug, setSlug] = useState(row.slug)
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})
  const [showFeatures, setShowFeatures] = useState(false)
  // What each plan really includes (Plans page). Null until loaded, or when
  // the plans migration hasn't run — then requires_tier is the default.
  const [plan, setPlan] = useState<Record<string, string[]> | null>(null)
  const [composer, setComposer] = useState<Composer>(() => ({ ...options.defaults, variant: row.signals.personalEmail ? 'reviewed' : 'trial' }))
  const [bccText, setBccText] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [preview, setPreview] = useState<{ subject: string; html: string; to: string; replyTo: string; from: string } | null>(null)
  const [previewErr, setPreviewErr] = useState('')
  const [mobile, setMobile] = useState(false)
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState('')
  const closeRef = useRef<HTMLButtonElement>(null)
  const noteRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let alive = true
    getPlans().then((r) => { if (alive) setPlan(r.plan) }).catch(() => { /* fall back to requires_tier */ })
    return () => { alive = false }
  }, [])
  const included = (f: ComposerOptions['features'][number], t: string) =>
    plan ? (plan[t] || []).includes(f.key) : inTier(f.requiresTier, t)

  const variant = options.variants.find((v) => v.key === composer.variant)
  const payloadComposer = useMemo(() => ({ ...composer, bcc: bccText.split(',').map((s) => s.trim()).filter(Boolean) }), [composer, bccText])

  // Live preview, debounced.
  useEffect(() => {
    let alive = true
    const t = setTimeout(async () => {
      try {
        const r = await api.post(`/super-admin/signups/${row.id}/preview-email`, { email: payloadComposer, trialDays, slug })
        if (alive) { setPreview(r.data); setPreviewErr('') }
      } catch (err) {
        if (alive) setPreviewErr(errMsg(err, "Couldn't build the preview"))
      }
    }, 350)
    return () => { alive = false; clearTimeout(t) }
  }, [row.id, payloadComposer, trialDays, slug])

  const grouped = useMemo(() => {
    const m = new Map<string, ComposerOptions['features']>()
    for (const f of options.features) m.set(f.category, [...(m.get(f.category) || []), f])
    return [...m.entries()]
  }, [options.features])
  const featureOn = (f: ComposerOptions['features'][number]) => overrides[f.key] ?? included(f, tier)
  const offCount = options.features.filter((f) => included(f, tier) && !featureOn(f)).length
  const extraCount = options.features.filter((f) => !included(f, tier) && featureOn(f)).length

  const insertVar = (v: string) => {
    const el = noteRef.current
    const token = `{{${v}}}`
    if (!el) return setComposer((c) => ({ ...c, note: c.note + token }))
    const { selectionStart: a, selectionEnd: b } = el
    const next = composer.note.slice(0, a) + token + composer.note.slice(b)
    setComposer((c) => ({ ...c, note: next }))
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = a + token.length })
  }

  const sendTest = async () => {
    setTesting(true)
    try {
      const r = await api.post(`/super-admin/signups/${row.id}/test-email`, { email: payloadComposer })
      notify(`Test sent to ${r.data.sentTo}`)
    } catch (err) {
      notify(errMsg(err, "Couldn't send the test"), 'error')
    } finally {
      setTesting(false)
    }
  }

  const approve = async () => {
    setBusy(true)
    setError('')
    try {
      const features: Record<string, boolean> = {}
      for (const f of options.features) {
        const def = included(f, tier)
        if (overrides[f.key] !== undefined && overrides[f.key] !== def) features[f.key] = overrides[f.key]
      }
      await api.post(`/super-admin/signups/${row.id}/approve`, { tier, trialDays, slug, features, email: payloadComposer, sendEmail })
      notify(`${row.companyName} approved — building the workspace now`)
      onApproved()
      onClose()
    } catch (err) {
      setError(errMsg(err, "Couldn't approve this signup"))
    } finally {
      setBusy(false)
    }
  }

  const field = 'w-full h-9 rounded-lg border border-slate-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
  const h3 = 'text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-2'
  const Sig = ({ ok, warn, children }: { ok?: boolean; warn?: boolean; children: React.ReactNode }) => (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${warn ? 'text-amber-700 bg-amber-50 border-amber-200' : ok ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-red-700 bg-red-50 border-red-200'}`}>
      {warn ? <ExclamationTriangleIcon className="w-3.5 h-3.5" /> : ok ? <CheckIcon className="w-3.5 h-3.5" /> : <XMarkIcon className="w-3.5 h-3.5" />}{children}
    </span>
  )

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-labelledby="approve-title" className="absolute top-0 right-0 bottom-0 w-full max-w-[1100px] bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 id="approve-title" className="text-lg font-semibold text-slate-900 truncate">Approve {row.companyName}</h2>
            <p className="text-sm text-slate-500 truncate">{row.contact.name} · {row.contact.email}{row.contact.phone ? ` · ${row.contact.phone}` : ''} · signed up {ago(row.createdAt)}</p>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[420px_1fr]">
          {/* configuration */}
          <div className="overflow-y-auto px-5 py-4 space-y-6 border-b lg:border-b-0 lg:border-r border-slate-200">
            <section>
              <h3 className={h3}>Checks at signup</h3>
              <div className="flex flex-wrap gap-1.5">
                {row.signals.personalEmail && <Sig warn>Personal email</Sig>}
                <Sig ok={row.signals.emailConfirmed}>{row.signals.emailConfirmed ? 'Email confirmed' : 'Email not confirmed'}</Sig>
                <Sig ok={row.signals.captcha}>{row.signals.captcha ? 'Captcha passed' : 'No captcha'}</Sig>
                {row.signals.ip && <span className="text-xs text-slate-500 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50">IP {row.signals.ip}</span>}
              </div>
              {(row.industry || row.employeeCount) && <p className="text-xs text-slate-500 mt-2">{[row.industry, row.employeeCount ? `${row.employeeCount}+ people` : null].filter(Boolean).join(' · ')}</p>}
              {row.review?.decision === 'asked' && <p className="text-xs text-slate-600 mt-2 border-l-2 border-amber-400 pl-2">You asked {ago(row.review.at)}: “{row.review.note}”</p>}
            </section>

            <section>
              <h3 className={h3}>Account</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label htmlFor="ap-tier" className="text-xs font-semibold text-slate-600">Plan</label>
                  <select id="ap-tier" className={field} value={tier} onChange={(e) => { setTier(e.target.value); setOverrides({}) }}>
                    {options.tiers.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label htmlFor="ap-days" className="text-xs font-semibold text-slate-600">Trial length</label>
                  <select id="ap-days" className={field} value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))}>
                    {options.trialLengths.map((d) => <option key={d} value={d}>{d} days</option>)}
                  </select>
                </div>
                <div className="col-span-2 space-y-1">
                  <label htmlFor="ap-slug" className="text-xs font-semibold text-slate-600">Web address</label>
                  <div className="flex items-center h-9 rounded-lg border border-slate-300 bg-white px-2.5 focus-within:ring-2 focus-within:ring-blue-500">
                    <input id="ap-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30))} className="flex-1 min-w-0 bg-transparent outline-none font-mono text-sm" />
                    <span className="font-mono text-xs text-slate-400">.{PORTAL_DOMAIN}</span>
                  </div>
                  {slug !== row.slug && <p className="text-xs text-amber-700">They chose <span className="font-mono">{row.slug}</span>. Changing it is fine before the database is built.</p>}
                </div>
              </div>
            </section>

            <section>
              <h3 className={h3}>Features to start with</h3>
              <div className="flex items-start gap-3 py-2">
                <div className="flex-1">
                  <b className="block text-sm font-semibold text-slate-900">Everything in {tierLabel(tier)}</b>
                  <span className="text-xs text-slate-500">{offCount || extraCount ? `${offCount ? `${offCount} turned off` : ''}${offCount && extraCount ? ', ' : ''}${extraCount ? `${extraCount} extra turned on` : ''}` : 'Adjust below if they don’t need something yet'}</span>
                </div>
                <button type="button" onClick={() => setShowFeatures((v) => !v)} className="text-xs font-semibold text-blue-600 hover:underline" aria-expanded={showFeatures}>{showFeatures ? 'Hide' : 'Adjust'}</button>
              </div>
              {showFeatures && (
                <div className="mt-1 space-y-3 max-h-72 overflow-y-auto pr-1">
                  {grouped.map(([cat, list]) => (
                    <div key={cat}>
                      <div className="text-[11px] font-semibold text-slate-500 mb-1">{cat}</div>
                      {list.map((f) => (
                        <div key={f.key} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-800 truncate">{f.name}</div>
                            {!included(f, tier) && <div className="text-[11px] text-slate-400">{plan ? `Not in ${tierLabel(tier)}` : `Normally ${tierLabel(f.requiresTier)} and up`}</div>}
                          </div>
                          <Switch on={featureOn(f)} onChange={(v) => setOverrides((o) => ({ ...o, [f.key]: v }))} label={f.name} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h3 className={h3}>Welcome email</h3>
              <div className="flex items-center gap-3 mb-3">
                <Switch on={sendEmail} onChange={setSendEmail} label="Send the welcome email" />
                <span className="text-sm text-slate-700">Send the welcome email</span>
              </div>
              <fieldset disabled={!sendEmail} className={`space-y-3 ${sendEmail ? '' : 'opacity-50'}`}>
                <div className="space-y-1">
                  <label htmlFor="ap-var" className="text-xs font-semibold text-slate-600">Template</label>
                  <select id="ap-var" className={field} value={composer.variant} onChange={(e) => setComposer((c) => ({ ...c, variant: e.target.value, subject: null }))}>
                    {options.variants.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label htmlFor="ap-subj" className="text-xs font-semibold text-slate-600">Subject</label>
                  <input id="ap-subj" className={field} value={composer.subject ?? variant?.subject ?? ''} onChange={(e) => setComposer((c) => ({ ...c, subject: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label htmlFor="ap-note" className="text-xs font-semibold text-slate-600">Personal note <span className="font-normal text-slate-400">(optional)</span></label>
                  <textarea id="ap-note" ref={noteRef} rows={4} className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" value={composer.note} onChange={(e) => setComposer((c) => ({ ...c, note: e.target.value }))} placeholder={`Thanks for signing up, ${(row.contact.name || '').split(' ')[0] || 'there'}. I've turned on…`} />
                  <div className="flex flex-wrap gap-1">
                    {options.variables.map((v) => (
                      <button key={v} type="button" onClick={() => insertVar(v)} className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100">{`{{${v}}}`}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <span className="text-xs font-semibold text-slate-600">Sections</span>
                  {([['app', 'App setup — QR code and store links'], ['firstSteps', 'First three steps'], ['support', 'Questions / support line']] as const).map(([k, label]) => (
                    <div key={k} className="flex items-center gap-3">
                      <Switch on={composer.sections[k]} onChange={(v) => setComposer((c) => ({ ...c, sections: { ...c.sections, [k]: v } }))} label={label} />
                      <span className="text-sm text-slate-700">{label}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label htmlFor="ap-reply" className="text-xs font-semibold text-slate-600">Reply-to</label>
                    <input id="ap-reply" className={field} value={composer.replyTo ?? ''} onChange={(e) => setComposer((c) => ({ ...c, replyTo: e.target.value || null }))} />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="ap-bcc" className="text-xs font-semibold text-slate-600">BCC</label>
                    <input id="ap-bcc" className={field} value={bccText} onChange={(e) => setBccText(e.target.value)} placeholder="sales@citstime.app" />
                  </div>
                </div>
              </fieldset>
            </section>
          </div>

          {/* preview */}
          <div className="overflow-y-auto bg-slate-100 px-5 py-4 flex flex-col gap-3 min-h-[420px]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div role="group" aria-label="Preview width" className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                <button type="button" aria-pressed={!mobile} onClick={() => setMobile(false)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md ${!mobile ? 'bg-slate-900 text-white' : 'text-slate-600'}`}><ComputerDesktopIcon className="w-4 h-4" />Desktop</button>
                <button type="button" aria-pressed={mobile} onClick={() => setMobile(true)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md ${mobile ? 'bg-slate-900 text-white' : 'text-slate-600'}`}><DevicePhoneMobileIcon className="w-4 h-4" />Phone</button>
              </div>
              <button type="button" onClick={sendTest} disabled={testing || !sendEmail} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50">
                <PaperAirplaneIcon className="w-4 h-4" />{testing ? 'Sending…' : 'Send a test to me'}
              </button>
            </div>
            {!sendEmail ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-500">No welcome email will be sent. They'll sign in with the password they chose.</div>
            ) : (
              <div className={`mx-auto w-full ${mobile ? 'max-w-[380px]' : 'max-w-[640px]'} transition-[max-width]`}>
                <div className="rounded-t-xl bg-white border border-slate-200 border-b-0 px-4 py-2.5 text-xs text-slate-600 grid gap-0.5">
                  <div><span className="text-slate-400">From</span> CITS Time &lt;{preview?.from ?? 'no-reply@citstime.app'}&gt;</div>
                  <div><span className="text-slate-400">To</span> {preview?.to ?? row.contact.email}{bccText ? <span className="text-slate-400"> · BCC {bccText}</span> : null}</div>
                  <div><span className="text-slate-400">Reply-to</span> {preview?.replyTo ?? composer.replyTo}</div>
                  <div className="font-semibold text-slate-900 text-sm mt-0.5">{preview?.subject ?? '…'}</div>
                </div>
                {previewErr ? (
                  <div className="rounded-b-xl border border-slate-200 bg-white p-6 text-sm text-red-600">{previewErr}</div>
                ) : (
                  <iframe title="Welcome email preview" srcDoc={preview?.html ?? ''} sandbox="" className="block w-full h-[760px] rounded-b-xl border border-slate-200 bg-white" />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex flex-wrap items-center gap-2">
          {error ? <p className="text-sm text-red-600" role="alert">{error}</p> : <p className="text-xs text-slate-500">Approving builds the workspace now. You'll see each step under “Setting up”.</p>}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="px-3 h-9 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-50">Cancel</button>
            <button type="button" onClick={approve} disabled={busy || slug.length < 3} className="inline-flex items-center gap-1.5 px-4 h-9 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              <CheckIcon className="w-4 h-4" />{busy ? 'Approving…' : 'Approve & build'}
            </button>
          </div>
        </div>
      </aside>
    </div>
  )
}
